import React, { useEffect, useCallback, useMemo } from "react";
import {
  Toggle,
  Notification,
  NotificationVariants,
  Iconography,
  IconographySizes,
} from "@hid-galaxy-ui/galaxy-react";
import { useAppDispatch, useAppSelector } from "../../../../../../redux";
import { Tab, Tabs, Box } from "@mui/material";
import { useSearchParams } from "react-router-dom";
import { PARTNER_PROGRAM_TAB_INDEX_BY_KEY } from "./partnerProgramTabs";
import Overview from "./overview";
import Calculator from "./calculator";
import PerformanceTrend from "./performance-trend";
import BenefitsLog from "./benefits-log";
import { getPartnerLicenceListSagaAction } from "../../../../../../redux/saga/partner-program/licence-management/saga-actions";
import {
  getPartnerTierListSagaAction,
  getConsentStatusSagaAction,
  setConsentSagaAction,
} from "../../../../../../redux/saga/partner-program/saga-actions";
import { partnerTierSliceAction } from "../../../../../../redux/slices/partner-program";
import "./PartnerProgram.scss";
import { getPartnerEngagementCategoryListSagaAction } from "../../../../../../redux/saga/partner-program/engagement-configuration/saga-actions";
import { getPartnerRegionalMultiplierListSagaAction } from "../../../../../../redux/saga/partner-program/regional-multipliers/saga-actions";
import { usePartnerTabPermission } from "../../../../../../contexts/PartnerPermissionContext";
import { useSessionCanEditModule } from "../../../../../../hooks/useSessionPermissions";
import { PARTNER_MANAGEMENT_PERMISSION_KEYS as PM_KEYS } from "../../../../../../utils/permissions/partnerManagementPermissionKeys";
import { useUnsavedChanges } from "../unsaved-changes/UnsavedChangesContext";

const TabPanel: React.FC<{
  index: number;
  value: number;
  children: React.ReactNode;
}> = ({ index, value, children }) => {
  return (
    <Box 
      sx={{ 
        mt: 2,
        display: value === index ? 'block' : 'none'
      }}
    >
      {children}
    </Box>
  );
};

const PartnerProgram = () => {
  const { readOnly: tabReadOnly } = usePartnerTabPermission();
  const canEditProgram = useSessionCanEditModule(PM_KEYS.ADMIN_PARTNER_PROGRAM);
  const programReadOnly = tabReadOnly || !canEditProgram;

  const [searchParams] = useSearchParams();
  const requestedTab = searchParams.get("tab");
  const [ppTab, setPpTab] = React.useState<number>(
    () => PARTNER_PROGRAM_TAB_INDEX_BY_KEY[requestedTab ?? ""] ?? 0
  );

  useEffect(() => {
    const tabIndex = PARTNER_PROGRAM_TAB_INDEX_BY_KEY[requestedTab ?? ""];
    if (tabIndex !== undefined) setPpTab(tabIndex);
  }, [requestedTab]);
  const [toasts, setToasts] = React.useState<any[]>([]);
  const previousPartnerIdRef = React.useRef<number | undefined>(undefined);
  const dispatch = useAppDispatch();
  const { requestNavigation } = useUnsavedChanges();
  const { partnerDetails } = useAppSelector((state) => state.partner_details);
  const {
    consentStatus,
    consentLoading,
    setConsentLoading,
    consentError,
    partnerProgramConsentEligible,
  } = useAppSelector((state) => state.partner_program);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  useEffect(() => {
    dispatch(getPartnerTierListSagaAction(1));
    dispatch(getPartnerLicenceListSagaAction(1));
    dispatch(getPartnerEngagementCategoryListSagaAction());
    dispatch(getPartnerRegionalMultiplierListSagaAction(partnerDetails?.ID));
  }, [dispatch]);

  // Only clear ineligible lock when switching to a different partner (not on tab remount).
  useEffect(() => {
    const id = partnerDetails?.ID;
    if (id == null) return;
    if (
      previousPartnerIdRef.current !== undefined &&
      previousPartnerIdRef.current !== id
    ) {
      dispatch(partnerTierSliceAction.setPartnerProgramConsentEligible(null));
    }
    previousPartnerIdRef.current = id;
  }, [dispatch, partnerDetails?.ID]);

  // Fetch consent status on mount when partner ID is available
  useEffect(() => {
    if (partnerDetails?.ID) {
      dispatch(getConsentStatusSagaAction(partnerDetails.ID));
    }
  }, [dispatch, partnerDetails?.ID]);

  useEffect(() => {
    if (!consentError) return;

    setToasts((prev) => [
      ...prev,
      {
        id: Math.floor(Math.random() * 1000),
        title: "",
        message: consentError,
        variant: NotificationVariants.Error,
        icon: <Iconography icon="ban" size={IconographySizes.Medium} />,
      },
    ]);

    // Clear after pushing toast so it doesn't re-trigger on re-mounts.
    dispatch(partnerTierSliceAction.setConsentError(null));
  }, [consentError]);

  // Calculate if opt-in based on consent status
  const isOptIn = useMemo(() => {
    return consentStatus?.consent_type === "OptIn";
  }, [consentStatus]);

  // Handle consent toggle
  const handleConsentToggle = useCallback(
    (e: any) => {
      if (!partnerDetails?.ID) return;

      const isChecked = e.target.checked;
      const newConsentType = isChecked ? "OptIn" : "OptOut";

      dispatch(
        setConsentSagaAction({
          partner_id: partnerDetails.ID,
          consent_type: newConsentType,
        })
      );
    },
    [dispatch, partnerDetails?.ID]
  );

  return (
    <div className="partner-program-container">
      <Notification toasts={toasts} setToasts={setToasts} />
      <div className="partner-program-header">
        <div className="header-left">
          <h1 className="header-title">Partner Program</h1>
          <p className="header-subtitle">
            Calculate partner's yearly tier progress and points
          </p>
        </div>
        <div className="header-right">
          <span className="status-label">Status (Opt In/Opt Out)</span>
          <Toggle
            id="consent-toggle"
            labelON="Opt In"
            labelOFF="Opt Out"
            onClick={handleConsentToggle}
            toggled={isOptIn}
            disabled={
              programReadOnly ||
              consentLoading ||
              setConsentLoading ||
              !partnerDetails?.ID ||
              partnerProgramConsentEligible === false
            }
          />
        </div>
      </div>
      <Tabs
        value={ppTab}
        onChange={(_, v) => {
          if (v === ppTab) return;
          // Only warn when leaving Benefits Log with unsaved changes.
          if (ppTab === 3) {
            requestNavigation(() => setPpTab(v));
            return;
          }
          setPpTab(v);
        }}
        aria-label="Partner Program Tabs"
        className="pp-config-tabs"
        variant="scrollable"
        scrollButtons="auto"
      >
        <Tab label="Performance Trend" />
        <Tab label="Overview" />
        <Tab label="Calculator (Running Year)" />
        <Tab label="Benefits Log" className="pp-benefits-log-tab" />
      </Tabs>
      <TabPanel index={0} value={ppTab}>
        <PerformanceTrend readOnly={programReadOnly} />
      </TabPanel>
      <TabPanel index={1} value={ppTab}>
        <Overview readOnly={programReadOnly} />
      </TabPanel>
      <TabPanel index={2} value={ppTab}>
        <Calculator readOnly={programReadOnly} />
      </TabPanel>
      <TabPanel index={3} value={ppTab}>
        <BenefitsLog readOnly={programReadOnly} />
      </TabPanel>
    </div>
  );
};

export default PartnerProgram;